<script lang="ts">
export interface NavDrawerProps {
  offset?: number
  width?: number
}
</script>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted } from 'vue'
import { NAV_DRAWER_WIDTH } from './constants'
import { closeSlideover, NAV_DRAWER_MARKER, setMenuAimEnabled, slideoverMenu } from './slideover'

defineOptions({
  inheritAttrs: false,
})

const props = withDefaults(defineProps<NavDrawerProps>(), {
  offset: 0,
  width: NAV_DRAWER_WIDTH,
})

const isOpen = computed(() => slideoverMenu.value !== null)

const wrapperStyle = computed(() => ({
  transform: `translateX(${props.offset}px)`,
}))

onMounted(() => setMenuAimEnabled(true))
onBeforeUnmount(() => {
  setMenuAimEnabled(false)
  closeSlideover()
})
</script>

<template>
  <div
    class="fixed inset-y-0 left-0 flex z-50 pointer-events-none transform transition-transform duration-200 ease-out"
    :style="wrapperStyle"
  >
    <Transition
      appear
      enter-active-class="transition-transform ease-out duration-300 will-change-transform"
      enter-from-class="-translate-x-full"
      enter-to-class="translate-x-0"
      leave-active-class="transition-transform ease-in duration-200 will-change-transform"
      leave-from-class="translate-x-0"
      leave-to-class="-translate-x-full"
    >
      <div
        v-if="isOpen"
        v-bind="$attrs"
        :data-el="NAV_DRAWER_MARKER"
        class="relative flex flex-col focus:outline-hidden bg-white h-full border-r border-gray-300 pointer-events-auto"
        :style="{ width: `${width}px` }"
      >
        <TransitionGroup name="nav-drawer-content" tag="div">
          <div
            :key="String(slideoverMenu ?? '')"
            class="font-display text-xs relative max-h-screen overflow-y-auto light-scrollbar z-10"
          >
            <slot :content-key="slideoverMenu" />
          </div>
        </TransitionGroup>
      </div>
    </Transition>
  </div>
</template>

<style>
.nav-drawer-content-move,
.nav-drawer-content-enter-active,
.nav-drawer-content-leave-active {
  transition:
    transform 0.1s ease-out,
    opacity 0.1s ease-out;
}
.nav-drawer-content-leave-to {
  opacity: 0;
  transform: translateX(-20px);
}
.nav-drawer-content-enter-from {
  opacity: 0;
  transform: translateX(20px);
}
.nav-drawer-content-leave-active {
  position: absolute;
}
</style>
